import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import SendToMobileIcon from "@mui/icons-material/SendToMobile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TableContainer from "Common/Tables/StringsFiltersTable/TableContainer";
import ActionButton from "components/Buttons/ActionButton";
import StatusChip from "components/Chips/StatusChip";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import {
  DynamicMenuItem,
  MenuItemConfig,
} from "components/Menuitem/DynamicMenuItem";
import ListingPageContainer from "components/PageContainer/ListingPageContainer";
import SearchBox from "components/SearchBox/SearchBox";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";
import useClearStateOnNavigation from "CustomHooks/useClearStateOnNavigation";
import usePermission from "CustomHooks/usePermission";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { app_modules } from "helpers/apps_modules_list";
import moment from "moment";
import { useCallback, useEffect, useMemo } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import {
  exportEnquiriesCSV,
  fetchEnquiries,
  updateEnquiryStage,
} from "slices/enquiry/thunk";
import Swal from "sweetalert2";
import { showConfirm, showCustomSwal } from "utils/swalHelper";

const Enquiry = () => {
  document.title = "Enquiry List";
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();

  // Redux selector for enquiry state
  const selectEnquiries = createSelector(
    (state: any) => state.Enquiry,
    (enquiry) => ({
      enquiries: enquiry.enquiryList,
      rowCount: enquiry.rowCount,
      loading: enquiry.loading,
      error: enquiry.error,
    })
  );

  const { enquiries, rowCount, loading, error } = useSelector(selectEnquiries);

  // Use custom hook to fetch and access permissions
  const { canView, canCreate, canUpdate } = usePermission(
    app_modules?.order?.app_name,
    app_modules?.order?.modules?.enquiry
  );

  // 🔥 Use preserved table state hook
  const tableState = usePreservedTableState({
    key: "enquiry",
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  // Clear state when navigating from create action (not edit)
  useClearStateOnNavigation(location, resetState);

  // Fetch enquiries using thunk
  useEffect(() => {
    dispatch(
      fetchEnquiries({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      })
    );
  }, [dispatch, pageIndex, pageSize, sorting, search, filterValue]);

  // ✅ Debounce search input to actual search
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const trimmedInput = searchInput.trim();

      const dateMatch = trimmedInput.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

      if (dateMatch) {
        const [, day, month, year] = dateMatch;
        const isoDate = `${year}-${month}-${day}`;
        setSearch(isoDate);
      } else {
        setSearch(trimmedInput);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput, setSearch]);

  // Handle export
  const handleExport = async () => {
    // Show loading modal
    showCustomSwal({
      title: "Exporting...",
      text: "Please wait while we export your data.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const { blob, filename } = await exportEnquiriesCSV({
        search,
        filters: filterValue,
      });

      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Replace loading modal with success modal
      showCustomSwal({
        icon: "success",
        title: "Export Completed",
        text: "Enquiries exported successfully.",
      });
    } catch (error: any) {
      console.error("Export failed", error);
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.error ||
        error?.message ||
        "Failed to export CSV";

      // Replace loading modal with error modal
      showCustomSwal({
        icon: "error",
        title: "Export Failed",
        text: errorMessage,
      });
    }
  };

  // Format date as DD/MM/YY
  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    try {
      return moment(dateString).format("DD/MM/YY");
    } catch {
      return dateString;
    }
  };

  // Handle update enquiry stage
  const handleUpdateEnquiryStage = useCallback(
    async (enquiryId: string, stage: string) => {
      try {
        await dispatch(updateEnquiryStage({ enquiryId, stage })).unwrap();
        const stageMessage =
          stage === "closed"
            ? "Enquiry closed successfully"
            : "Enquiry stage updated successfully";
        toast.success(stageMessage);

        // Refresh the list
        dispatch(
          fetchEnquiries({
            page: pageIndex + 1,
            pageSize,
            sort: sortField,
            order: sortField ? sortDirection : "",
            search,
            filters: filterValue,
          })
        );
      } catch (err: any) {
        const errorMessage =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.error ||
          err?.message ||
          "Failed to update enquiry stage";
        toast.error(errorMessage);
      }
    },
    [
      dispatch,
      pageIndex,
      pageSize,
      sortField,
      sortDirection,
      search,
      filterValue,
    ]
  );

  // Create menu items for DynamicMenuItem
  const menuItems: MenuItemConfig[] = useMemo(() => {
    const items: MenuItemConfig[] = [
      {
        icon: VisibilityIcon,
        label: "View",
        permission: canView,
        link: (enquiry: any) =>
          `/enquiry-details/${enquiry.enquiry_id || enquiry.id}`,
        color: "themeColor",
      },
      {
        icon: EditIcon,
        label: "Edit",
        permission: canUpdate,
        link: (enquiry: any) =>
          `/enquiry-form/${enquiry.enquiry_id || enquiry.id}`,
        color: "themeColor",
      },
      {
        icon: SendToMobileIcon,
        label: "Request For Quotation",
        permission: canUpdate,
        onClick: async (enquiry: any) => {
          const result = await showConfirm(
            "Are you sure you want to request for quotation?",
            "Request For Quotation?",
            "Yes",
            "No"
          );

          if (result.isConfirmed) {
            handleUpdateEnquiryStage(
              enquiry.enquiry_id || enquiry.id,
              "Request For Quotation"
            );
          }
        },
        color: "themeColor",
      },
      {
        icon: CancelIcon,
        label: "Close Enquiry",
        permission: canUpdate,
        onClick: async (enquiry: any) => {
          const result = await showConfirm(
            "Are you sure you want to close this enquiry?",
            "Close Enquiry?",
            "Yes",
            "No"
          );

          if (result.isConfirmed) {
            handleUpdateEnquiryStage(
              enquiry.enquiry_id || enquiry.id,
              "closed"
            );
          }
        },
        color: "error",
      },
      {
        icon: OpenInFullIcon,
        label: "Open",
        permission: canUpdate,
        onClick: async (enquiry: any) => {
          const result = await showConfirm(
            "Are you sure you want to open this enquiry?",
            "Open Enquiry?",
            "Yes",
            "No"
          );

          if (result.isConfirmed) {
            handleUpdateEnquiryStage(
              enquiry.enquiry_id || enquiry.id,
              "Pending"
            );
          }
        },
        color: "success",
      },
    ];
    return items;
  }, [canUpdate, canView, handleUpdateEnquiryStage]);

  const DynamicAction = useMemo(() => {
    // Only show Action column if user has at least one permission
    const hasPermission = canUpdate || canView;
    if (!hasPermission) {
      return [];
    }

    // Always show Actions column (as per user's comment)
    return [
      {
        header: "Actions",
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          stickyRight: true,
        },
        cell: ({ row }: { row: any }) => {
          // Filter menu items based on enquiry stage
          const stage = row.original.stage?.toLowerCase();

          // If stage is "request for quotation", only show "View" action
          if (stage === "request for quotation") {
            const filteredMenuItems = menuItems.filter((item) => {
              return item.label === "View";
            });
            return (
              <DynamicMenuItem
                row={row.original}
                menuItems={filteredMenuItems}
              />
            );
          }

          // For other stages, apply normal filtering
          const filteredMenuItems = menuItems.filter((item) => {
            // Show "Edit" action only if stage is "pending"
            if (item.label === "Edit") {
              return stage === "pending" || stage === "site visit completed";
            }
            // Show "Request for Quotation" action only if stage is "pending"
            if (item.label === "Request For Quotation") {
              return stage === "pending"  || stage === "site visit completed";
            }
            // Hide "Close Enquiry" action if stage is already "closed"
            if (item.label === "Close Enquiry") {
              return stage === "pending" || stage === "site visit completed";
            }
            // Show "Open" action only if stage is "closed"
            if (item.label === "Open") {
              return stage === "closed";
            }
            return true;
          });

          return (
            <DynamicMenuItem row={row.original} menuItems={filteredMenuItems} />
          );
        },
      },
    ];
  }, [menuItems]);

  const columns = useMemo(
    () => [
      {
        header: "Sr No.",
        accessorKey: "sr_no",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => row.original.sr_no || "-",
      },
      {
        header: "Enquiry No.",
        accessorKey: "enquiry_number",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/enquiry/dropdown/enquiry_number",
        cell: ({ row }: { row: any }) => row.original.enquiry_number || "-",
      },
      {
        header: "Enquiry Date",
        accessorKey: "enquiry_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const date = row.original.enquiry_date
            ? new Date(row.original.enquiry_date).toISOString().split("T")[0]
            : "-";
          return date;
        },
      },
      {
        header: "Customer Name",
        accessorKey: "customer_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/enquiry/dropdown/customer_name",
        cell: ({ row }: { row: any }) => {
          return row.original.customer_details?.customer_name || "-";
        },
      },
      {
        header: "Phone Number",
        accessorKey: "contact_person_phone_no",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          return row.original.customer_details?.contact_person_phone_no || "-";
        },
      },
      {
        header: "Site Visit",
        accessorKey: "site_visit_required",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown",
        filterFetchUrl: "/enquiry/dropdown/site_visit_required",
        cell: ({ row }: { row: any }) => {
          const value = row.getValue("site_visit_required");
          return value ? "Yes" : "No";
        },
      },
      {
        header: "Stage",
        accessorKey: "stage",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/enquiry/dropdown/stage",
        cell: ({ row }: { row: any }) => {
          // return  row.original.stage || "-"
          return (
            //  <div className="text-center">
            <StatusChip status={row.original.stage}>
              {row.original.stage}
            </StatusChip>
            //  </div>
          );
        },
      },
      {
        header: "Staff Name",
        accessorKey: "staff_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/enquiry/dropdown/staff_name",
        cell: ({ row }: { row: any }) => {
          // Get staff name from site_visit object (denormalized field)
          // staff_details is an object, but staff_name is the denormalized string field
          return row.original.site_visit?.staff_name || "-";
        },
      },
      {
        header: "Source",
        accessorKey: "enquiry_source",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "/enquiry/dropdown/source_of_enquiry",
        cell: ({ row }: { row: any }) => {
          return row.original.enquiry_source || "-";
        },
      },
      {
        header: "Site Visit Date",
        accessorKey: "site_visit_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          return row.original.site_visit?.site_visit_date
            ? new Date(row.original.site_visit?.site_visit_date)
                .toISOString()
                .split("T")[0]
            : "-";
        },
      },
      {
        header: "Remark",
        accessorKey: "remark",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <TruncatedTextCell
            text={row?.original?.remark}
            maxWidth="300px"
            maxLines={4}
            rowIndex={row.index}
            tooltipId={`remark-tooltip-${row.index}`}
          />
        ),
      },

      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name =
            row.original.created_by_user?.username ||
            row.original.created_by_user?.first_name ||
            "N/A";
          const date = row.original.created_at
            ? new Date(row.original.created_at).toISOString().split("T")[0]
            : "N/A";
          return `${name} - ${date}`;
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name =
            row.original.updated_by_user?.username ||
            row.original.updated_by_user?.first_name ||
            "N/A";
          const date = row.original.updated_at
            ? new Date(row.original.updated_at).toISOString().split("T")[0]
            : "N/A";
          return `${name} - ${date}`;
        },
      },
      ...DynamicAction,
    ],
    [DynamicAction]
  );

  return (
    <ListingPageContainer>
      <style>
        {`
          .truncated-text-cell-wrapper {
            min-width: 0 !important;
            display: -webkit-box !important;
            -webkit-box-orient: vertical !important;
            overflow: hidden !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
            white-space: normal !important;
            line-height: 1.5em !important;
          }
        `}
      </style>
      <Row id="enquiryList">
        <Col lg={12}>
          <Card>
            <Card.Header>
              <Row className="align-items-center g-2">
                <Col lg={3} className="me-auto">
                  <h6 className="card-title mb-0">Enquiry</h6>
                </Col>

                {/* Search Input Box (reusable)*/}
                <Col xl={2} md={3}>
                  <SearchBox value={searchInput} onChange={setSearchInput} />
                </Col>

                <Col className="col-md-auto">
                  <div className="hstack gap-2">
                    {canView && (
                      <ActionButton
                        label="Export"
                        icon="bi bi-box-arrow-up"
                        handleClick={handleExport}
                        variant="light"
                      />
                    )}
                    {canCreate && (
                      <ActionButton
                        label="Create Enquiry"
                        icon="bi bi-plus-circle"
                        navigateLink="/enquiry-form"
                        variant="primary"
                      />
                    )}
                  </div>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body className="mt-3">
              <LoaderErrorWrapper loading={loading} error={error}>
                <TableContainer
                  columns={columns}
                  data={enquiries}
                  totalRecords={rowCount}
                  pageIndex={pageIndex}
                  pageSize={pageSize}
                  onPageChange={setPageIndex}
                  onPageSizeChange={setPageSize}
                  columnFilters={columnFilters}
                  setColumnFilters={setColumnFilters}
                  sorting={sorting}
                  onSortingChange={setSorting}
                  isBordered={false}
                  isPagination={true}
                  isGlobalFilter={false}
                  SearchPlaceholder="Search Enquiries..."
                  manualSorting={true}
                  colValue={setFilterValue}
                />
              </LoaderErrorWrapper>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ListingPageContainer>
  );
};

export default Enquiry;
